/**
 * Chooses which questions to show, and in what order.
 *
 * The goal is simple: questions he gets wrong come back sooner and more often,
 * questions he has learned still come back but rarely, and every session mixes
 * in something new so it does not become boring.
 */
import type { ProgressState, Question, QuestionProgress } from '../types';
import { getEntry, needsPractice, priorityFor } from './progress';

export function shuffle<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const PRIORITY_WEIGHT: Record<ReturnType<typeof priorityFor>, number> = {
  high: 8,
  medium: 5,
  review: 3,
  none: 2,
  low: 1,
};

/** Higher score = more useful to practise right now. */
export function scoreQuestion(entry: QuestionProgress, now: number): number {
  let score = PRIORITY_WEIGHT[priorityFor(entry)];

  // Due for review, or not due yet.
  score *= entry.dueAt <= now ? 1.6 : 0.6;

  // Something he has never seen is worth showing.
  if (entry.seen === 0) score *= 1.4;

  // Do not repeat the same item twice within a few minutes.
  if (entry.lastSeenAt !== null && now - entry.lastSeenAt < 5 * 60 * 1000) score *= 0.2;

  return Math.max(score, 0.05);
}

/** Weighted random pick without replacement. */
function weightedSample(pool: Question[], weights: Map<string, number>, count: number): Question[] {
  const remaining = [...pool];
  const picked: Question[] = [];
  while (picked.length < count && remaining.length > 0) {
    let total = 0;
    for (const q of remaining) total += weights.get(q.id) ?? 1;
    let roll = Math.random() * total;
    let index = remaining.length - 1;
    for (let i = 0; i < remaining.length; i += 1) {
      roll -= weights.get(remaining[i].id) ?? 1;
      if (roll <= 0) {
        index = i;
        break;
      }
    }
    picked.push(remaining[index]);
    remaining.splice(index, 1);
  }
  return picked;
}

function weightsFor(questions: Question[], progress: ProgressState, now: number): Map<string, number> {
  const map = new Map<string, number>();
  for (const q of questions) {
    map.set(q.id, scoreQuestion(getEntry(progress, 'question', q.id), now));
  }
  return map;
}

/** Everything he has answered wrongly and has not mastered again yet. */
export function selectMistakes(questions: Question[], progress: ProgressState, limit = 20): Question[] {
  const pool = questions.filter((q) => needsPractice(getEntry(progress, 'question', q.id)));
  pool.sort((a, b) => {
    const ea = getEntry(progress, 'question', a.id);
    const eb = getEntry(progress, 'question', b.id);
    if (eb.wrong !== ea.wrong) return eb.wrong - ea.wrong;
    return (ea.lastSeenAt ?? 0) - (eb.lastSeenAt ?? 0);
  });
  return pool.slice(0, limit);
}

/**
 * Today's Practice: a mix of things he struggles with, things he has not seen
 * recently, and a couple he already knows so the session starts confidently.
 */
export function selectDaily(questions: Question[], progress: ProgressState, count = 10): Question[] {
  if (questions.length === 0) return [];
  const now = Date.now();
  const weights = weightsFor(questions, progress, now);

  const struggling: Question[] = [];
  const fresh: Question[] = [];
  const known: Question[] = [];

  for (const q of questions) {
    const entry = getEntry(progress, 'question', q.id);
    if (needsPractice(entry)) struggling.push(q);
    else if (entry.seen === 0) fresh.push(q);
    else if (entry.status === 'learned' || entry.status === 'familiar') known.push(q);
    else fresh.push(q);
  }

  const wantStruggling = Math.min(struggling.length, Math.round(count * 0.5));
  const wantKnown = Math.min(known.length, Math.max(1, Math.round(count * 0.2)));
  const wantFresh = Math.max(0, count - wantStruggling - wantKnown);

  const chosen = [
    ...weightedSample(struggling, weights, wantStruggling),
    ...weightedSample(fresh, weights, wantFresh),
    ...weightedSample(known, weights, wantKnown),
  ];

  // Top up from anything left if one bucket was too small.
  if (chosen.length < count) {
    const used = new Set(chosen.map((q) => q.id));
    const rest = questions.filter((q) => !used.has(q.id));
    chosen.push(...weightedSample(rest, weights, count - chosen.length));
  }

  return shuffle(chosen).slice(0, count);
}

/** Learning Mode / topic practice: weakest and newest first, then the rest. */
export function selectForLearning(
  questions: Question[],
  progress: ProgressState,
  count = 15,
): Question[] {
  if (questions.length === 0) return [];
  const now = Date.now();
  const weights = weightsFor(questions, progress, now);
  return weightedSample(questions, weights, Math.min(count, questions.length));
}

/**
 * Practice test: 40 questions spread across the topics, like the real test.
 * If the bank has fewer than 40 questions, every question is used once — the
 * screen tells the learner how many questions the test actually has.
 */
export function selectTest(questions: Question[], count = 40): Question[] {
  if (questions.length <= count) return shuffle(questions);

  const byCategory = new Map<string, Question[]>();
  for (const q of questions) {
    const list = byCategory.get(q.category) ?? [];
    list.push(q);
    byCategory.set(q.category, list);
  }
  for (const [key, list] of byCategory) byCategory.set(key, shuffle(list));

  const picked: Question[] = [];
  const keys = shuffle([...byCategory.keys()]);
  // Round-robin across topics so every topic is represented.
  let exhausted = false;
  while (picked.length < count && !exhausted) {
    exhausted = true;
    for (const key of keys) {
      const list = byCategory.get(key)!;
      const next = list.pop();
      if (next) {
        picked.push(next);
        exhausted = false;
        if (picked.length === count) break;
      }
    }
  }
  return shuffle(picked);
}

/** Questions that are due for review right now. */
export function selectDue(questions: Question[], progress: ProgressState, limit = 20): Question[] {
  const now = Date.now();
  return questions
    .filter((q) => {
      const entry = getEntry(progress, 'question', q.id);
      return entry.seen > 0 && entry.dueAt <= now;
    })
    .sort((a, b) => getEntry(progress, 'question', a.id).dueAt - getEntry(progress, 'question', b.id).dueAt)
    .slice(0, limit);
}
